<script lang="ts">
  /**
   * The challenges tab: everything played on a deal everybody shares,
   * against a clock. A line saying what the tab is, the game's challenge
   * cards, an optional column beside them, and a closing line. TwinTen's
   * `ChallengesTab` layout (Interlock's Daily).
   *
   * - `intro`: one sentence over the cards.
   * - `cards`: the game's `ChallengeCard`s (one per challenge that runs: a
   *   daily with its `endsAt` countdown, a weekly, an event). A card's body
   *   is the game's own strip (TwinTen's week, Interlock's best line), in
   *   the card's `children`.
   * - `aside`: a column beside the cards from 1024px (a `Leaderboard`), under
   *   them on a phone.
   * - `footer`: one quiet, centred line at the end ("A card shows only while
   *   its challenge runs").
   *
   * From 1024px the cards flow into as many 22rem columns as fit; with an
   * `aside`, the cards take the left column and the aside the right.
   *
   * Nothing here fetches. Test ids: `testid` on the page (default
   * `ui-challenges`); `testids.intro`, `testids.footer` (no defaults).
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly cards: Snippet;
    readonly intro?: string | undefined;
    readonly footer?: string | undefined;
    readonly aside?: Snippet | undefined;
    readonly testid?: string | undefined;
    readonly testids?: { readonly intro?: string; readonly footer?: string } | undefined;
  }
  const { cards, intro, footer, aside, testid = 'ui-challenges', testids }: Props = $props();
</script>

<div class="ui-challenges" class:ui-challenges--aside={aside !== undefined} data-testid={testid}>
  {#if intro !== undefined}
    <p class="ui-challenges-intro" data-testid={testids?.intro}>{intro}</p>
  {/if}

  <div class="ui-challenges-cards">{@render cards()}</div>

  {#if aside !== undefined}
    <div class="ui-challenges-aside">{@render aside()}</div>
  {/if}

  {#if footer !== undefined}
    <p class="ui-challenges-footer" data-testid={testids?.footer}>{footer}</p>
  {/if}
</div>

<style>
  .ui-challenges {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    padding: var(--ui-space-2) var(--ui-gutter) var(--ui-space-5);
    font-family: var(--ui-font);
  }

  .ui-challenges-intro {
    margin: 0 var(--ui-space-1);
    font: var(--ui-text-label);
    line-height: 1.4;
    color: var(--ui-ink-2);
  }

  .ui-challenges-cards,
  .ui-challenges-aside {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
  }

  .ui-challenges-footer {
    margin: var(--ui-space-1) 0 0;
    text-align: center;
    font: var(--ui-text-caption);
    font-weight: 700;
    color: var(--ui-ink-2);
  }

  @media (min-width: 1024px) {
    .ui-challenges {
      padding: var(--ui-space-5) var(--ui-gutter-wide) var(--ui-space-6);
    }

    .ui-challenges-cards {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));
      align-items: start;
    }

    /* With an aside: the cards on the left, the aside on the right; the
       intro and the footer across both. */
    .ui-challenges--aside {
      display: grid;
      grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
      column-gap: var(--ui-space-6);
      align-items: start;
    }

    .ui-challenges--aside .ui-challenges-intro,
    .ui-challenges--aside .ui-challenges-footer {
      grid-column: 1 / -1;
    }

    .ui-challenges--aside .ui-challenges-cards {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
